export const PROMPT_START_TEXT = "Tell me more about this member's"
export type PredefinedPrompt = {
  text: string
  llmHelpText?: string | string[]
}

const CASE_CONFERENCE_SECTIONS: Record<string, string> = {
  Summary: `Start with one sentence that names the member's cancer diagnosis, including stage and/or metastasis locations if available. Then provide a 30-second skim summary that lets a medical director immediately understand what has changed since the last case conference, what is risky, and what open questions there are on next steps for this patient. Notes any needs described as urgent.`,
  Treatment: `Describe the member's current treatment. Include a brief history of previous treatments if applicable to current symptoms the member is experiencing (eg "Member was previously on Oxaliplatin and is still experiencing chemo-induced peripheral neuropathy (CIPN)").`,
  Comorbidities: `If the member is diagnosed with any of these comorbidities, state whether it is currently well controlled: chronic kidney disease (CKD), congestive heart failure (CHF), diabetes mellitus (DM), cirrhosis, chronic obstructive pulmonary disease (COPD), hypertension (HTN), and/or any behavioral health condition. If the condition is not well controlled, include the associated meds/management. If the member is diagnosed with other comorbidities that are associated with a current goal, then provide status of the comorbid and associated meds/management.`,
  Symptoms: `Describe any current reported symptoms that are noted as requiring further evaluation or intervention. Check all recent subtasks and communications that mention symptom ID, distress thermometer, assess & intervene, and/or behavioral health.`,
  TOCs: `Describe any transition of care (TOC) events within the past 2 months.`,
  'Function and Life Planning': `Describe if the member's functional status is impacting their ability to perform ADLs and if they need additional support. Describe if the member is noted as needing support with medical directives, living will, POA, and/or a palliative care referral.`,
}

/**
 * For multiprompt, it is helpful to tell the LLM what sections are covered in other prompts to prevent
 * duplicate response info.
 * @param currentSection
 * @param sections
 */
function buildCrossReference(
  currentSection: string,
  sections: string[]
): string {
  const others = sections.filter((s) => s !== currentSection)
  return `Do not cover topics from the following other sections of this prep document: ${others.join(
    ', '
  )}.`
}

/**
 *
 * @param sectionName
 * @param baseText
 */
function buildCaseConferenceSectionPrompt(
  sectionName: string,
  baseText: string
): string {
  return `${sectionName}\n${baseText} This is one section of a longer prep document. Only include information relevant to this specific prompt. ${buildCrossReference(
    sectionName,
    Object.keys(CASE_CONFERENCE_SECTIONS)
  )}`
}

/**
 * Extracts the section title from a subprompt string.
 * Convention: the first line of each subprompt is its display title.
 * @param sectionText
 */
export function parseSectionTitle(sectionText: string): string {
  return sectionText.trim().split('\n')[0].trim()
}

export const PREDEFINED_PROMPTS: PredefinedPrompt[] = [
  {
    text: 'Diagnosis, staging history, and current treatment intent',
    llmHelpText: `
    Concisely list this member's diagnosis (as listed in the clinical summary),
    current treatment intent (as listed in the care plan), and staging history.
    * Provide ONLY those pieces of information.
    * Do NOT include additional findings.
    * Check communications notes and subtask notes.
    `,
  },
  {
    text: 'Past and current cancer treatment',
    llmHelpText: `
    Concisely describe this member's past and current cancer treatment.
    * Only describe cancer diagnosis and treatments.
    * Do NOT describe other medical history or treatments for non-cancer conditions.
    `,
  },
  {
    text: 'Symptom and side effect history (past three months)',
    llmHelpText: `Concisely list this member's symptoms and side effects for the past three months. Only provide information about symptoms and side effects.`,
  },
  {
    text: 'ED and hospitalization history (past six months)',
    llmHelpText: `Concisely list this member's ED and hospitalization history for the past six months. Only provide information about ED visits and hospitalizations.`,
  },
  {
    text: 'Non-cancer medical conditions and management',
    llmHelpText:
      "Concisely describe this member's non-cancer medical conditions and their current management.",
  },
  {
    text: 'Known social needs and Thyme Care support for them (past three months)',
    llmHelpText:
      "Make sure to check for recent subtasks, communications, and indicated social needs in the member's profile.",
  },
  {
    text: 'Known behavioral health needs and Thyme Care support for them (past three months)',
  },
  {
    text: 'Known caregivers and support system',
    llmHelpText: `
        Describe this member's social support system.
        * Include groups such as friends or religious communities as well as specific individuals.
        * List any known caregivers.
        * Do NOT include individuals listed as Care Partners, as these are care management providers, not social contacts.
        * Do NOT include other information about their social needs.
    `,
  },
  {
    text: 'Topics counseled on and outcomes of those conversations',
    llmHelpText:
      'List the topics that this member has been counseled on. For each topic, describe the topic or need and the outcome of the conversation.',
  },
  {
    text: 'Thyme Care support provided or planned for each care goal',
  },
  {
    text: 'Case Conference Prep Document (Multi-prompt)',
    llmHelpText: Object.entries(CASE_CONFERENCE_SECTIONS).map(
      ([name, content]) => buildCaseConferenceSectionPrompt(name, content)
    ),
  },
  {
    text: 'Case Conference Prep Document (Prompt 1)',
    llmHelpText: `
    * Create a prep document for an interdisciplinary case conference about a cancer patient with a
    medical director and a team of nurses and social workers.
    * Summarize the most pressing clinical and/or social needs this patient is currently experiencing.
    * Make sure to include any transitions of care (TOC) events.
    * Make sure to include a summary of the last successful phone call and/or text message (if any) since the last case conference.
    * Categorize the member's engagement since the last case conference as "very engaged", "somewhat engaged", "not very engaged", or "no contact".
    * Highlight any concerns that may lead to this patient going to the emergency department in the next 30 days or experiencing other adverse clinical or social outcomes.
    `,
  },
  {
    text: 'Case Conference Prep Document (Prompt 2)',
    llmHelpText: `
    * Create a prep document for an interdisciplinary case conference about a cancer patient with a medical director and a team of nurses and social workers that reports known information from the chart.
    * Review all of the available information about this patient.
    * Describe the most serious documented current clinical and/or social needs for this patient.
    * Make sure to note any transitions of care (TOC) events documented in the chart.
    * Make sure to note the last successful phone call and/or text message (if any) since the last case conference.
    * Categorize the member's engagement since the last case conference as "very engaged", "somewhat engaged", "not very engaged", or "no contact".
    * Report any documented concerns that may lead to this patient going to the emergency department in the next 30 days or experiencing other adverse clinical or social outcomes.

    Here is an EXAMPLE of the format of the prep document. DO NOT use the patient information contained within it. -

    The patient is a 68-year-old male with metastatic non–small-cell lung cancer (adenocarcinoma, stage IV) with bone and liver metastases and severe COPD with chronic hypoxic respiratory failure requiring continuous home oxygen (2–3 L NC). He is on second-line therapy (docetaxel ± ramucirumab), with last infusion three weeks ago. Recent imaging showed mixed response with stable lung disease and mild liver progression.
    He has a high symptom burden, including worsening dyspnea, fatigue, cough, cancer-related thoracic and hip pain (on opioids), poor appetite, and recent weight loss. Inhaler adherence has been inconsistent, and pulmonology follow-up has not been re-established. Hypertension is well controlled; no diabetes, CKD, or active cardiac disease is documented.
    Functional status is ECOG 2–3 with increasing IADL dependence. He lives alone with limited support, has transportation barriers, and reports anxiety related to dyspnea and fear of ED use.
    Goals-of-care and serious illness education were previously introduced but not recently revisited, and understanding appears limited. Palliative care was consulted once but is not established longitudinally.
    Since the last case conference, he had one ED visit for a COPD exacerbation treated with steroids and nebulizers and discharged the same day. he remains at high risk for ED utilization in the next 30 days due to severe COPD, progressive cancer, recent exacerbation, high symptom burden, limited support, and anxiety.
    The last successful outreach was a TC nurse phone call 10 days ago, during which he reported persistent dyspnea, poor sleep, and anxiety but declined pulmonology follow-up. One additional call and two texts were unanswered. Engagement is somewhat engaged.
    `,
  },
  {
    text: 'Case Conference Prep (Prompt 3)',
    llmHelpText: `
    Please provide a summary of the following that is concise enough to be presented in less than three minutes.

    * One-line description: member age, gender, primary cancer diagnosis, and top 2 high-risk comorbidities
    * Last Thyme Care contact: timing and nature of most recent interaction
    * Oncologic condition and prognosis: current treatment status and trajectory
    * High-risk comorbid conditions: control status, limited to 2 most impactful
    * Treatment adherence: oncology appointments, PCP, specialists, medications, infusions
    * Functional status: current performance status and recent changes
    * Summary of member goals and their status
    `,
  },
  {
    text: 'Member story',
    llmHelpText: `You are a Member-Story Writer. You work for Thyme Care which is a remote care navigation company and write member stories for partners to demonstrate the value of our interventions.

We use proactive symptom monitoring, care coordination, and social support to help members manage complex health conditions and avoid unnecessary hospitalizations.

Your job is to turn member interactions into a concise narrative that captures what we did, what the member did, and the effect.

Choose the best episode from the provided context that illustrates a clear problem, intervention, and outcome.

Write the story in three labeled sections: Problem, Intervention, Outcome. Use only those headings.

Word count: 150-250 words.

Source Data
Look at all available source data except Goals or Claims. Do NOT look at Goals or Claims.

Voice & Tone
• Plain English. Human and empathetic. Strong verbs. No metaphors or flowery language.
• Person-centered: use person-first language (e.g., "a 54-year-old member with breast cancer"). Reference ourselves as "Thyme Care" or "the Thyme Care team."

Sequence Rules
• Tell the story in strict chronological order: We did A, then B, then C, which led to X.
• Use consistent language and ensure clarity.
• Use explicit sequence cues: "First... Next... Then... Finally...". Do not merge steps.

Criteria for a Good Story
• Clear member actions and outcomes.
• Vivid but concise details that clarify the narrative.
Specific details about objects ( Foley catheter, Skilled Nursing, etc.) are good; vague terms ( "treatment," "support," "help," etc.) are not.

#### FOCUS ON CLINICAL OUTCOMES WHEN POSSIBLE. ####
Content Rules
1) Use ONLY the supplied context; do NOT add any information or assumptions.
2) Narrative only, no bullet points.
3) No names or specific individuals; third person; use role titles (e.g., "Thyme Care nurse navigator...").
4) Focus on business-relevant outcomes: cost avoidance, continuity of care, risk reduction, utilization, member satisfaction.
5) Do not include locations or other identifying details.
6) State only actions the member actually completed due to our intervention (scheduled, attended, started therapy, completed ACP, used Virtual Urgent Care, etc.). Use past tense. Do NOT include intentions or feelings as outcomes (e.g., "considered," "planned," "felt," "more informed") unless tied to a concrete completed action or measurable result.


Formatting
• Problem: one brief paragraph.
• Intervention: one brief paragraph describing what we did, in sequence, and how it enabled the action(s).
• Outcome: one brief paragraph describing the member action(s) and measurable outcome(s). Ensure clear flow from problem to intervention to outcome.

Story Categories for emphasis: preventing admissions using proactive monitoring,  Urgent Care Support, Behavioral Health, Social Needs Support, Advanced Care Planning, Care Coordination with other providers.
FOCUS ON CLINICAL OUTCOMES AND STORIES THAT SHOW CLEAR PROBLEM-INTERVENTION-OUTCOME SEQUENCES.`,
  },
]
